<template>
  <RouterLink
    :to="`/event/${eventItem.id}`"
    class="block w-full aspect-16/9 relative rounded-md overflow-hidden group border border-surface-grey mb-10"
  >
    <img
      :src="photoToUrl(eventItem.photo)"
      :alt="eventItem.info1"
      class="w-full h-full object-cover object-center transition-transform duration-300 group-hover:scale-105"
    />
      <div 
        v-if="badges[0]" 
        :class="'absolute top-2 right-2 text-headings text-xs px-2 py-1 rounded font-semibold ' + badges[0].class"
      >
        <template v-if="badges[0].localeKey">
          {{ $t(badges[0].localeKey) }}
        </template>
        <template v-else>
          {{ badges[0].string || badges[0] }}
        </template>
      </div>
    <div
      class="absolute bottom-0 left-0 w-full bg-gradient-to-t from-black/70 to-transparent p-4 md:p-8 flex flex-col gap-2"
    >
      <h2 :class="['event-title text-xl font-semibold text-white line-clamp-2', eventNameVisibilityClass]">
        {{ eventItem.info1 }}
      </h2>

      <div
        class="flex flex-col md:flex-row text-surface-light text-sm gap-2 md:gap-4"
      >
        <div :class="['flex items-center', dateVisibilityClass]">
          <Calendar class="h-4 w-4 mr-1 fill-current" />
          <EventDate :event="eventItem" />
        </div>
        <div :class="['flex items-center', venueVisibilityClass]">
          <MapPin class="h-4 w-4 mr-1 fill-white/80" />
          <span>{{ venue ? venue.name : '' }}</span>
        </div>
      </div>
    </div>
  </RouterLink>
</template>

<script setup lang="ts">
  import { computed } from 'vue';

  import Calendar from '@/components/icons/CalendarIcon.vue';
  import MapPin from '@/components/icons/MapPinIcon.vue';
  import { useGeneralStore } from '@/stores/general';
  import type { EventItem } from '@/types';
  import { getBadgesWithVisibility, getDateVisibilityClass, getEventNameVisibilityClass, getVenueVisibilityClass } from '@/utils/eventItemUtil';
  import { photoToUrl } from '@/utils/photoToUrl';

  import EventDate from './EventDate.vue';

  const props = defineProps<{ eventItem: EventItem; }>();

  const generalStore = useGeneralStore();

  const venues = computed(() => generalStore.venues());

  const venue = computed(() =>
    venues.value?.find(v => v.id === props.eventItem.venue)
  );
  
  const isExternalTicketsale = computed(() =>
    generalStore.customer().external_ticketsale === 1 && !!props.eventItem.external_ticketsale_url
  );

  // Computed badge logic
  const badges = computed(() => getBadgesWithVisibility(props.eventItem, { externalTicketsaleActive: isExternalTicketsale.value }));

  const eventNameVisibilityClass = computed(() => getEventNameVisibilityClass(props.eventItem));
  const dateVisibilityClass = computed(() => getDateVisibilityClass(props.eventItem));
  const venueVisibilityClass = computed(() => getVenueVisibilityClass(props.eventItem));
</script>
